import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const StockChart = ({ stockData }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, date: '', stock: 0 });

  useEffect(() => {
    if (!stockData?.data?.length || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = 192;
    const margin = { top: 20, right: 50, bottom: 28, left: 40 };
    const inner = { w: width - margin.left - margin.right, h: height - margin.top - margin.bottom };

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const parseDate = d => new Date(d);
    const data = stockData.data.map(d => ({ date: parseDate(d.date), stock: d.stock }));

    const x = d3.scaleTime().domain(d3.extent(data, d => d.date)).range([0, inner.w]);
    const y = d3.scaleLinear().domain([0, d3.max(data, d => d.stock) * 1.05]).nice().range([inner.h, 0]);

    // Y grid lines only
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(5).tickSize(-inner.w).tickFormat(''))
      .selectAll('line')
      .attr('stroke', '#4b5563')
      .attr('stroke-opacity', 0.3);
    g.select('.grid .domain').remove();

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${inner.h})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat('%d %b')).tickSize(0))
      .call(g => g.select('.domain').attr('stroke', '#4b5563').attr('stroke-opacity', 0.5))
      .selectAll('text')
      .attr('fill', '#a3b18a')
      .attr('font-size', '10px')
      .style('font-feature-settings', '"tnum"');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickSize(0).tickFormat(d3.format(',')))
      .call(g => g.select('.domain').remove())
      .selectAll('text')
      .attr('fill', '#a3b18a')
      .attr('font-size', '10px')
      .style('font-feature-settings', '"tnum"');

    // Gradient fill
    const gradId = 'stock-gradient';
    const defs = svg.append('defs');
    const grad = defs.append('linearGradient').attr('id', gradId).attr('x1', '0').attr('y1', '0').attr('x2', '0').attr('y2', '1');
    grad.append('stop').attr('offset', '0%').attr('stop-color', '#48a877').attr('stop-opacity', 0.15);
    grad.append('stop').attr('offset', '100%').attr('stop-color', '#48a877').attr('stop-opacity', 0);

    // Area
    const area = d3.area()
      .x(d => x(d.date))
      .y0(inner.h)
      .y1(d => y(d.stock))
      .curve(d3.curveMonotoneX);
    g.append('path').datum(data).attr('d', area).attr('fill', `url(#${gradId})`);

    // Line
    const line = d3.line().x(d => x(d.date)).y(d => y(d.stock)).curve(d3.curveMonotoneX);
    g.append('path').datum(data).attr('d', line).attr('fill', 'none').attr('stroke', '#48a877').attr('stroke-width', 2);

    // Direct labels at first and last points
    const first = data[0];
    const last = data[data.length - 1];

    const labelStyle = (sel) => sel
      .attr('fill', '#fff')
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .style('font-feature-settings', '"tnum"');

    g.append('text')
      .attr('x', x(first.date))
      .attr('y', y(first.stock) - 10)
      .attr('text-anchor', 'start')
      .text(first.stock.toLocaleString())
      .call(labelStyle);

    g.append('text')
      .attr('x', x(last.date))
      .attr('y', y(last.stock) - 10)
      .attr('text-anchor', 'end')
      .text(last.stock.toLocaleString())
      .call(labelStyle);

    // Net change annotation with halo
    const netChange = stockData.summary.net_change;
    const sign = netChange >= 0 ? '+' : '';
    g.append('text')
      .attr('x', x(last.date))
      .attr('y', y(last.stock) + 16)
      .attr('text-anchor', 'end')
      .attr('fill', netChange >= 0 ? '#34d399' : '#f87171')
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .style('paint-order', 'stroke fill')
      .attr('stroke', '#1f2937')
      .attr('stroke-width', '3px')
      .style('font-feature-settings', '"tnum"')
      .text(`${sign}${netChange.toLocaleString()}`);

    // Voronoi hover
    const delaunay = d3.Delaunay.from(data, d => x(d.date), d => y(d.stock));
    const hoverCircle = g.append('circle').attr('r', 5).attr('fill', 'none').attr('stroke', '#48a877').attr('stroke-width', 2).style('opacity', 0);

    svg.append('rect')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .attr('width', inner.w)
      .attr('height', inner.h)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mx, my] = d3.pointer(event, g.node());
        const idx = delaunay.find(mx, my);
        const d = data[idx];
        hoverCircle.attr('cx', x(d.date)).attr('cy', y(d.stock)).style('opacity', 1);
        setTooltip({
          visible: true,
          x: x(d.date) + margin.left,
          y: y(d.stock) + margin.top,
          date: d.date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }),
          stock: d.stock.toLocaleString(),
        });
      })
      .on('mouseleave', () => {
        hoverCircle.style('opacity', 0);
        setTooltip(t => ({ ...t, visible: false }));
      });

    // Resize handler
    const ro = new ResizeObserver(() => {
      if (containerRef.current) {
        const newW = containerRef.current.clientWidth;
        if (Math.abs(newW - width) > 10) {
          svg.selectAll('*').remove();
          // Re-trigger effect
          setTooltip(t => ({ ...t }));
        }
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [stockData]);

  if (!stockData?.data) return null;

  const summary = stockData.summary;
  const base = summary.current_stock - summary.net_change;
  const changePercentage = base !== 0 ? ((summary.net_change / base) * 100).toFixed(1) : 0;
  const isPositive = summary.net_change >= 0;

  const headline = isPositive
    ? `El stock creció ${Math.abs(changePercentage)}% en 90 días`
    : `El stock cayó ${Math.abs(changePercentage)}% en 90 días`;

  return (
    <div className="glass-effect rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">{headline}</h3>
        <div className={`flex items-center space-x-1 text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          <span>{isPositive ? '↗' : '↘'}</span>
          <span className="font-medium" style={{ fontFeatureSettings: '"tnum"' }}>{Math.abs(changePercentage)}%</span>
        </div>
      </div>

      {/* Chart */}
      <div ref={containerRef} className="h-48 mb-4 relative">
        <svg ref={svgRef} className="w-full h-full" />
        {tooltip.visible && (
          <div
            className="absolute pointer-events-none px-2 py-1 rounded text-xs"
            style={{
              left: tooltip.x,
              top: tooltip.y - 40,
              transform: 'translateX(-50%)',
              transition: 'left 200ms cubic-bezier(.4,0,.2,1), top 200ms cubic-bezier(.4,0,.2,1)',
              background: 'rgba(17,24,39,0.92)',
              border: '1px solid #48a877',
              color: '#fff',
              fontFeatureSettings: '"tnum"',
            }}
          >
            <div className="text-gray-400">{tooltip.date}</div>
            <div className="font-semibold">{tooltip.stock} vehículos</div>
          </div>
        )}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="text-center">
          <p className="text-gray-400">Stock Actual</p>
          <p className="font-semibold text-emerald-400" style={{ fontFeatureSettings: '"tnum"' }}>{summary.current_stock.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-400">Promedio</p>
          <p className="font-semibold text-white" style={{ fontFeatureSettings: '"tnum"' }}>{summary.avg_stock.toLocaleString()}</p>
        </div>
      </div>

      {/* Range / net-change footer */}
      <div className="mt-4 pt-4 border-t border-[#2a2926]">
        <div className="flex justify-between text-xs text-gray-400">
          <span>Rango: <span className="text-white font-medium" style={{ fontFeatureSettings: '"tnum"' }}>{summary.min_stock.toLocaleString()} – {summary.max_stock.toLocaleString()}</span></span>
          <span>Cambio neto: <span className={`font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`} style={{ fontFeatureSettings: '"tnum"' }}>
            {isPositive ? '+' : ''}{summary.net_change.toLocaleString()}
          </span></span>
        </div>
      </div>
    </div>
  );
};

export default StockChart;
